import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import TropicalBackground from '../components/TropicalBackground';
import { API_URL } from '../config/api';

/**
 * PreviewPage - Page d'activation du mode preview pour audit visuel
 * 
 * USAGE: Uniquement pour l'audit visuel du thème Tropical Fintech Premium
 * À désactiver en production
 */

const PreviewPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const startPreview = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/preview/start`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Preview started:', data);
        // Rediriger vers le dashboard
        window.location.href = '/dashboard';
      } else {
        setError('Erreur lors du démarrage du mode preview');
      }
    } catch (err) {
      console.error('Preview error:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <TropicalBackground className="min-h-screen flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl shadow-lg flex items-center justify-center">
          <span className="text-4xl">🎨</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Mode Audit Visuel
        </h1>
        <p className="text-gray-600 text-sm max-w-xs mx-auto">
          Démarrer une session de démonstration pour auditer le thème "Tropical Fintech Premium"
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600 space-y-2">
            <p>✅ Utilisateur de démo créé</p>
            <p>✅ Calendrier avec opérations</p>
            <p>✅ Objectifs d'épargne</p>
            <p>✅ Portefeuille</p>
            <p>✅ Session valide 2 heures</p>
          </div>
          
          {error && (
            <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <button
            onClick={startPreview}
            disabled={loading}
            className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 shadow-lg hover:shadow-xl'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Chargement...
              </span>
            ) : (
              'Démarrer l\'audit visuel'
            )}
          </button>
        </div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-8 text-xs text-gray-400"
      >
        Mode développement uniquement
      </motion.p>
    </TropicalBackground>
  );
};

export default PreviewPage;
